import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useLicense } from '../../context/LicenseContext';
import SiteMap from './SiteMap';
import { usePageCustomizer, CustomizeButton, CustomizerPanel, PageWidget } from '../../components/PageCustomizer';
import {
  generateHourlyPeopleCounts, generateDirectionalFlows, generateHeatmap,
  generatePersonDetections, generateLoiteringAlerts, generateAgeBreakdown,
  generateGenderBreakdown, generateClothingColors, generateTimelineEvents,
  heatmapColor, CLOTHING_COLORS, AGE_GROUP_LABELS, ZONES,
  PersonDetection, LoiteringAlert, TimelineEvent,
} from './AnalyticsService';

const VISION_WIDGETS: PageWidget[] = [
  { id:'live',      label:'Live Detection',  icon:'🔴', description:'Real-time AI detection feed', defaultVisible:true },
  { id:'history',   label:'History',         icon:'🕐', description:'Past detection events and timeline', defaultVisible:true },
  { id:'analytics', label:'Analytics',       icon:'📊', description:'People counting, heatmaps and trends', defaultVisible:true },
  { id:'map',       label:'Density Map',     icon:'🗺️', description:'Live occupancy heatmap across zones', defaultVisible:true },
];

const getToken = () => localStorage.getItem('lemai_token') || '';

// ── Types ─────────────────────────────────────────────────────────────────────

interface DahuaEvent {
  id: string; type: string; label: string;
  severity: 'low'|'medium'|'high'|'critical';
  channel: number; camera_name: string; timestamp: string;
  snapshot_url: string; details: Record<string,string>; source: string;
}

interface Integration {
  id: string; type: string; label: string; category: string; status: string;
  host: string; port: string; username?: string; password?: string;
  extra?: Record<string,string>;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const SEV_COLOR: Record<string,string> = { low:'#22c55e', medium:'#f59e0b', high:'#ef4444', critical:'#dc2626' };
const SEV_BG: Record<string,string> = { low:'rgba(34,197,94,0.1)', medium:'rgba(245,158,11,0.1)', high:'rgba(239,68,68,0.1)', critical:'rgba(220,38,38,0.15)' };
const TYPE_ICON: Record<string,string> = {
  VideoMotion:'💨', SmartMotionDetect:'🎯', FaceDetect:'👤', CrossLineDetection:'⚡',
  CrossRegionDetection:'🚨', VideoLoss:'📵', VideoBlind:'🙈', AlarmLocal:'🚨',
  ParkingDetection:'🚗', LeftDetection:'📦', TakenAwayDetection:'🏃', FallDetection:'⚠️',
  HumanDensityDetection:'👥', VehicleDetection:'🚙', NumberStat:'🔢',
};
const TYPE_LABEL: Record<string,string> = {
  VideoMotion:'Motion', SmartMotionDetect:'Smart Motion', FaceDetect:'Face Detection',
  CrossLineDetection:'Line Cross', CrossRegionDetection:'Region Intrusion',
  VideoLoss:'Video Loss', VideoBlind:'Camera Blind', AlarmLocal:'Local Alarm',
  ParkingDetection:'Parking', LeftDetection:'Left Object', TakenAwayDetection:'Taken Object',
  FallDetection:'Fall Detection', HumanDensityDetection:'Crowd Density',
  VehicleDetection:'Vehicle', NumberStat:'People Count',
};

function timeAgo(ts: string): string {
  try {
    const diff = (Date.now() - new Date(ts).getTime()) / 1000;
    if (diff < 60) return `${Math.round(diff)}s ago`;
    if (diff < 3600) return `${Math.round(diff/60)}m ago`;
    return `${Math.round(diff/3600)}h ago`;
  } catch { return ts; }
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

function useDahuaIntegrations(): Integration[] {
  const stored = localStorage.getItem('lemai_integrations');
  if (!stored) return [];
  try { return (JSON.parse(stored) as Integration[]).filter(i => i.category==='cctv' && i.type==='dahua' && i.status==='connected'); }
  catch { return []; }
}

function useEventPolling(integrations: Integration[], active: boolean) {
  const [events, setEvents] = useState<DahuaEvent[]>([]);
  const [pollingStatus, setPollingStatus] = useState<'idle'|'polling'|'error'>('idle');
  const [lastPoll, setLastPoll] = useState<Date|null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>|null>(null);
  const seenIds = useRef<Set<string>>(new Set());

  const poll = useCallback(async () => {
    if (!active || integrations.length === 0) return;
    setPollingStatus('polling');
    try {
      for (const integ of integrations) {
        const host = integ.host || integ.extra?.host || '';
        const port = integ.port || integ.extra?.port || '80';
        const username = integ.username || integ.extra?.username || 'admin';
        const password = integ.password || integ.extra?.password || '';
        if (!host) continue;
        await fetch('/api/dahua/configure', {
          method:'POST', headers:{'Content-Type':'application/json', Authorization:`Bearer ${getToken()}`},
          body: JSON.stringify({ host, port, username, password }),
        });
        const r = await fetch('/api/dahua/events?limit=50', { headers:{ Authorization:`Bearer ${getToken()}` } });
        if (r.ok) {
          const data = await r.json();
          const incoming: DahuaEvent[] = data.events || [];
          const newEvts = incoming.filter(e => !seenIds.current.has(e.id));
          newEvts.forEach(e => seenIds.current.add(e.id));
          if (newEvts.length > 0) setEvents(prev => [...newEvts, ...prev].slice(0, 200));
        }
      }
      setLastPoll(new Date());
      setPollingStatus('idle');
    } catch { setPollingStatus('error'); }
  }, [active, integrations]);

  useEffect(() => {
    if (!active) return;
    poll();
    timerRef.current = setInterval(poll, 8000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [active, poll]);

  return { events, pollingStatus, lastPoll };
}

// ── Snapshot ──────────────────────────────────────────────────────────────────

function EventSnapshot({ url, alt }: { url: string; alt: string }) {
  const [src, setSrc] = useState('');
  const [err, setErr] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    fetch(url, { headers:{ Authorization:`Bearer ${getToken()}` }, signal: controller.signal })
      .then(r => r.blob()).then(b => setSrc(URL.createObjectURL(b))).catch(() => setErr(true));
    return () => controller.abort();
  }, [url]);
  if (err || !src) return <div style={{ width:'100%', height:'100%', background:'#0d0d0d', display:'flex', alignItems:'center', justifyContent:'center', color:'#444444', fontSize:'20px' }}>📷</div>;
  return <img src={src} alt={alt} style={{ width:'100%', height:'100%', objectFit:'cover' }} />;
}

// ── Clip player ───────────────────────────────────────────────────────────────

type ClipState = 'idle'|'loading'|'ready'|'error';

function EventClipPlayer({ event }: { event: DahuaEvent }) {
  const [state, setState] = useState<ClipState>('idle');
  const [videoUrl, setVideoUrl] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [clipSource, setClipSource] = useState('');
  const videoRef = useRef<HTMLVideoElement>(null);

  function loadClip() {
    setState('loading');
    const params = new URLSearchParams({ channel: String(event.channel), frames:'16', fps:'8', ...(event.timestamp ? { timestamp: event.timestamp } : {}) });
    fetch(`/api/dahua/clip?${params}`, { headers:{ Authorization:`Bearer ${getToken()}` } })
      .then(r => { setClipSource(r.headers.get('X-Clip-Source') || ''); if (!r.ok) return r.json().then(d => Promise.reject(d.error || `HTTP ${r.status}`)); return r.blob(); })
      .then(blob => { setVideoUrl(URL.createObjectURL(blob)); setState('ready'); })
      .catch(e => { setErrorMsg(String(e)); setState('error'); });
  }

  return (
    <div style={{ marginTop:8 }}>
      {state==='idle' && <button onClick={loadClip} style={{ background:'rgba(59,130,246,0.15)', border:'1px solid rgba(59,130,246,0.4)', color:'#93c5fd', padding:'4px 10px', borderRadius:4, fontSize:11, cursor:'pointer' }}>▶ View Clip</button>}
      {state==='loading' && <div style={{ color:'#555555', fontSize:11 }}>⏳ {clipSource==='recording' ? 'Fetching recording from NVR…' : 'Capturing live snippet…'}</div>}
      {state==='error' && <div style={{ color:'#ef4444', fontSize:11 }}>Clip unavailable — {errorMsg}</div>}
      {state==='ready' && videoUrl && (
        <video ref={videoRef} src={videoUrl} controls autoPlay loop style={{ width:'100%', maxHeight:180, borderRadius:4, background:'#000', marginTop:4 }} />
      )}
    </div>
  );
}

// ── Event type dropdown ───────────────────────────────────────────────────────

function EventTypeDropdown({ integrations, liveTypes, selected, onChange }: {
  integrations: Integration[]; liveTypes: string[]; selected: string[]; onChange: (v: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [available, setAvailable] = useState<string[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (async () => {
      if (integrations.length === 0) return;
      const integ = integrations[0];
      await fetch('/api/dahua/configure', { method:'POST', headers:{'Content-Type':'application/json', Authorization:`Bearer ${getToken()}`}, body:JSON.stringify({ host:integ.host||integ.extra?.host, port:integ.port||integ.extra?.port||'80', username:integ.username||integ.extra?.username||'admin', password:integ.password||integ.extra?.password }) });
      const r = await fetch('/api/dahua/event-types', { headers:{ Authorization:`Bearer ${getToken()}` } });
      if (r.ok) { const d = await r.json(); setAvailable(d.event_types || []); }
    })();
  }, [integrations]);

  const all = Array.from(new Set([...available, ...liveTypes])).sort();

  useEffect(() => {
    function handle(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const toggle = (t: string) => onChange(selected.includes(t) ? selected.filter(x => x!==t) : [...selected, t]);
  const label = selected.length === 0 ? 'All Analytics' : `${selected.length} filter${selected.length>1?'s':''} active`;

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{ background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.12)', color:'#cbd5e1', padding:'6px 12px', borderRadius:6, fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
        🔍 {label} {open ? '▲' : '▼'}
      </button>
      {open && (
        <div style={{ position:'fixed', zIndex:9999, background:'#111111', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, padding:8, minWidth:220, boxShadow:'0 8px 32px rgba(0,0,0,0.5)' }}>
          <div style={{ fontSize:10, color:'#555555', padding:'2px 8px 6px', fontWeight:700, letterSpacing:1, textTransform:'uppercase' }}>Analytics Filters</div>
          {all.length === 0 && <div style={{ color:'#555555', fontSize:12, padding:'4px 8px' }}>Loading event types…</div>}
          {all.map(t => (
            <label key={t} style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 8px', borderRadius:4, cursor:'pointer', color:'#cbd5e1', fontSize:12 }}>
              <input type="checkbox" checked={selected.includes(t)} onChange={() => toggle(t)} style={{ accentColor:'#3b82f6' }} />
              <span>{TYPE_ICON[t]||'•'} {TYPE_LABEL[t]||t}</span>
            </label>
          ))}
          {selected.length > 0 && (
            <button onClick={() => onChange([])} style={{ width:'100%', marginTop:6, background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.3)', color:'#fca5a5', borderRadius:4, padding:'4px 0', fontSize:11, cursor:'pointer' }}>Clear filters</button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Analytics helpers ─────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color }: { label:string; value:string|number; sub?:string; color?:string }) {
  return (
    <div style={{ background:'#111111', border:'1px solid #1a1a1a', borderRadius:8, padding:'14px 16px', flex:1, minWidth:100 }}>
      <div style={{ fontSize:11, color:'#555555', fontWeight:700, letterSpacing:1, textTransform:'uppercase', marginBottom:4 }}>{label}</div>
      <div style={{ fontSize:28, fontWeight:700, color: color||'#f1f5f9', lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:'#555555', marginTop:4 }}>{sub}</div>}
    </div>
  );
}

function MiniBar({ label, value, max, color, hex }: { label:string; value:number; max:number; color?:string; hex?:string }) {
  const pct = max > 0 ? (value/max)*100 : 0;
  return (
    <div style={{ marginBottom:8 }}>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#a0a0a0', marginBottom:3 }}>
        <span style={{ display:'flex', alignItems:'center', gap:6 }}>
          {hex && <span style={{ width:10, height:10, borderRadius:2, background:hex, display:'inline-block' }}/>}
          {label}
        </span>
        <span style={{ color:'#f1f5f9', fontWeight:600 }}>{value}</span>
      </div>
      <div style={{ height:6, background:'rgba(255,255,255,0.06)', borderRadius:3, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pct}%`, background: color||hex||'#3b82f6', borderRadius:3, transition:'width 0.6s ease' }}/>
      </div>
    </div>
  );
}

function HeatmapGrid({ seed }: { seed: number }) {
  const rows = 7; const cols = 12;
  const cells = generateHeatmap(rows, cols, seed);
  const zones = ['Main Entrance', 'Lobby', 'Council Chambers'];
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:`repeat(${cols}, 1fr)`, gap:2, borderRadius:6, overflow:'hidden' }}>
        {cells.map((c, i) => (
          <div key={i} title={`Zone density: ${Math.round(c.value*100)}%`} style={{ height:22, background: heatmapColor(c.value), borderRadius:2, transition:'background 0.3s' }}/>
        ))}
      </div>
      <div style={{ display:'flex', gap:4, marginTop:8, alignItems:'center', justifyContent:'flex-end' }}>
        {[0,0.25,0.5,0.75,1].map(v => (
          <div key={v} style={{ display:'flex', alignItems:'center', gap:3, fontSize:10, color:'#555555' }}>
            <div style={{ width:12, height:12, borderRadius:2, background: heatmapColor(v) }}/>
            {v===0?'Low':v===1?'High':''}
          </div>
        ))}
      </div>
      <div style={{ display:'flex', gap:8, marginTop:6 }}>
        {zones.map(z => <div key={z} style={{ fontSize:10, color:'#555555', background:'#111111', padding:'2px 6px', borderRadius:3 }}>{z}</div>)}
      </div>
    </div>
  );
}

function PeopleFlowChart({ date }: { date: string }) {
  const readings = generateHourlyPeopleCounts(date);
  const max = Math.max(...readings.map(r => r.count), 1);
  const w = 420; const h = 100; const pad = 20;
  const pts = readings.map((r, i) => {
    const x = pad + (i / (readings.length-1)) * (w - pad*2);
    const y = h - pad - (r.count/max) * (h - pad*2);
    return { x, y, r };
  });
  const path = pts.map((p, i) => `${i===0?'M':'L'}${p.x},${p.y}`).join(' ');

  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width:'100%', height:100 }}>
        <defs>
          <linearGradient id="flowGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4"/>
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0"/>
          </linearGradient>
        </defs>
        <path d={`${path} L${pts[pts.length-1].x},${h-pad} L${pts[0].x},${h-pad} Z`} fill="url(#flowGrad)"/>
        <path d={path} stroke="#3b82f6" strokeWidth="1.5" fill="none"/>
        {pts.filter((_,i) => i % 2 === 0).map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={3} fill="#3b82f6"/>
            <text x={p.x} y={h-4} textAnchor="middle" fontSize="7" fill="#555555">{p.r.timestamp.slice(0,2)}h</text>
          </g>
        ))}
      </svg>
      <div style={{ display:'flex', gap:12, marginTop:4 }}>
        {readings.slice(-3).map(r => (
          <div key={r.timestamp} style={{ fontSize:11, color:'#555555' }}>
            {r.timestamp}: <span style={{ color:'#f1f5f9', fontWeight:600 }}>{r.count}</span> ppl
            <span style={{ color:'#22c55e', marginLeft:4 }}>↑{r.in}</span>
            <span style={{ color:'#ef4444', marginLeft:2 }}>↓{r.out}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DirectionalFlowPanel() {
  const flows = generateDirectionalFlows();
  return (
    <div>
      {flows.map((f, i) => (
        <div key={i} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10, fontSize:12 }}>
          <div style={{ flex:1, textAlign:'right', color:'#a0a0a0' }}>{f.from}</div>
          <div style={{ display:'flex', alignItems:'center', gap:4, color:'#3b82f6', fontWeight:700, fontSize:14 }}>
            <span style={{ fontSize:10, color:'#555555' }}>{f.count}</span>
            →
          </div>
          <div style={{ flex:1, color:'#a0a0a0' }}>{f.to}</div>
          <div style={{ width:60, textAlign:'right' }}>
            <div style={{ height:5, background:'rgba(255,255,255,0.06)', borderRadius:3, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${f.pct}%`, background:'#3b82f6', borderRadius:3 }}/>
            </div>
            <span style={{ fontSize:10, color:'#555555' }}>{f.pct}%</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function LoiteringPanel({ alerts }: { alerts: LoiteringAlert[] }) {
  return (
    <div>
      {alerts.map(a => (
        <div key={a.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', background: a.severity==='high'?'rgba(239,68,68,0.08)':a.severity==='medium'?'rgba(245,158,11,0.08)':'rgba(255,255,255,0.04)', borderRadius:6, marginBottom:6, border:`1px solid ${a.severity==='high'?'rgba(239,68,68,0.2)':a.severity==='medium'?'rgba(245,158,11,0.2)':'rgba(255,255,255,0.08)'}` }}>
          <span style={{ fontSize:18 }}>⚠️</span>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:12, color:'#f1f5f9', fontWeight:600 }}>{a.zone}</div>
            <div style={{ fontSize:11, color:'#555555' }}>{a.camera} · {a.timestamp}</div>
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:13, fontWeight:700, color: a.severity==='high'?'#ef4444':'#f59e0b' }}>{Math.round(a.dwellSeconds/60)}m {a.dwellSeconds%60}s</div>
            <div style={{ fontSize:10, color:'#555555', textTransform:'uppercase', letterSpacing:0.5 }}>{a.severity}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function TimelinePanel({ events }: { events: TimelineEvent[] }) {
  const SEV: Record<string,string> = { info:'#3b82f6', low:'#22c55e', medium:'#f59e0b', high:'#ef4444', critical:'#dc2626' };
  return (
    <div style={{ maxHeight:320, overflowY:'auto' }}>
      {events.map(e => (
        <div key={e.id} style={{ display:'flex', gap:10, padding:'7px 4px', borderBottom:'1px solid #1a1a1a' }}>
          <div style={{ width:3, borderRadius:2, background: SEV[e.severity]||'#555555', alignSelf:'stretch', minHeight:24 }}/>
          <div style={{ flex:1 }}>
            <div style={{ display:'flex', justifyContent:'space-between' }}>
              <span style={{ fontSize:12, color:'#e5e5e5', fontWeight:500 }}>{e.label}</span>
              <span style={{ fontSize:11, color:'#555555' }}>{e.timestamp}</span>
            </div>
            <div style={{ fontSize:11, color:'#555555', marginTop:2 }}>{e.details}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PersonAttributeRow({ d }: { d: PersonDetection }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px', background:'#0d0d0d', borderRadius:6, marginBottom:6, border:'1px solid #1a1a1a', fontSize:12 }}>
      <div style={{ width:32, height:32, borderRadius:'50%', background:'rgba(59,130,246,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}>
        {d.gender==='female'?'👩':d.gender==='male'?'👨':'🧑'}
      </div>
      <div style={{ flex:1 }}>
        <div style={{ color:'#e5e5e5', fontWeight:600 }}>{d.camera} · {d.timestamp}</div>
        <div style={{ color:'#555555', marginTop:2 }}>
          {AGE_GROUP_LABELS[d.ageGroup]} · {d.topColor} top
          {d.hasBackpack && <span style={{ marginLeft:6 }}>🎒</span>}
          {d.hasBag && <span style={{ marginLeft:4 }}>👜</span>}
          {d.hasHat && <span style={{ marginLeft:4 }}>🧢</span>}
        </div>
      </div>
      <div style={{ textAlign:'right' }}>
        <div style={{ color: d.isLoitering?'#ef4444':'#22c55e', fontWeight:600 }}>{Math.round(d.dwellSeconds/60)}m {d.dwellSeconds%60}s</div>
        <div style={{ fontSize:10, color:'#555555' }}>{d.confidence}% conf</div>
      </div>
      {d.isLoitering && <span title="Loitering alert" style={{ fontSize:16 }}>⚠️</span>}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const ANALYTICS_TABS = ['Overview', 'People', 'Heatmap', 'Flow', 'Loitering', 'Timeline'] as const;
type AnalyticsTab = typeof ANALYTICS_TABS[number];

export default function SmartVision() {
  const { isDemoOrg } = useLicense();
  // Main tabs
  const [mainTab, setMainTab] = useState<'live'|'history'|'analytics'|'map'>('live');
  const { editing, setEditing, slots, isVisible: isVTab, orderedWidgets: orderedVWidgets, toggle: toggleVTab, moveUp: moveVUp, moveDown: moveVDown, reset: resetVLayout } = usePageCustomizer('vision', VISION_WIDGETS);

  // Live / Dahua state
  const integrations = useDahuaIntegrations();
  const { events, pollingStatus, lastPoll } = useEventPolling(integrations, mainTab === 'live');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedChannels, setSelectedChannels] = useState<number[]>([]);
  const [expandedId, setExpandedId] = useState<string|null>(null);

  // History state
  const [histDate, setHistDate] = useState(() => new Date().toISOString().slice(0,10));
  const [histFromHour, setHistFromHour] = useState(0);
  const [histToHour, setHistToHour] = useState(new Date().getHours());
  const [histEvents, setHistEvents] = useState<DahuaEvent[]>([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histChannel, setHistChannel] = useState(0);

  // Analytics state
  const [analyticsTab, setAnalyticsTab] = useState<AnalyticsTab>('Overview');
  const [analyticsDate, setAnalyticsDate] = useState(() => new Date().toISOString().slice(0,10));
  // Analytics data — only generated for demo org; real orgs show empty until Dahua AI is configured
  const personDetections = isDemoOrg ? generatePersonDetections(30) : [];
  const loiteringAlerts  = isDemoOrg ? generateLoiteringAlerts() : [];
  const timelineEvents   = isDemoOrg ? generateTimelineEvents(personDetections, loiteringAlerts) : [];
  const ageBreakdown     = isDemoOrg ? generateAgeBreakdown(personDetections) : { child: 0, young_adult: 0, adult: 0, senior: 0 };
  const genderBreakdown  = isDemoOrg ? generateGenderBreakdown(personDetections) : { male: 0, female: 0, unknown: 0 };
  const clothingColors   = isDemoOrg ? generateClothingColors(personDetections) : [];
  const todayCount       = isDemoOrg ? generateHourlyPeopleCounts(analyticsDate) : [];
  const totalPeople = todayCount.reduce((s, r) => s + r.count, 0);
  const totalIn     = todayCount.reduce((s, r) => s + r.in, 0);
  const totalOut    = todayCount.reduce((s, r) => s + r.out, 0);

  // Filtered events (live)
  const liveTypes = Array.from(new Set(events.map(e => e.type))).sort();
  const channels = Array.from(new Set(events.map(e => e.channel))).sort((a,b)=>a-b);
  const filtered = events.filter(e =>
    (selectedTypes.length === 0 || selectedTypes.includes(e.type)) &&
    (selectedChannels.length === 0 || selectedChannels.includes(e.channel))
  );

  async function loadHistory() {
    if (integrations.length === 0) return;
    setHistLoading(true);
    const integ = integrations[0];
    await fetch('/api/dahua/configure', { method:'POST', headers:{'Content-Type':'application/json', Authorization:`Bearer ${getToken()}`}, body:JSON.stringify({ host:integ.host||integ.extra?.host, port:integ.port||integ.extra?.port||'80', username:integ.username||integ.extra?.username||'admin', password:integ.password||integ.extra?.password }) });
    const params = new URLSearchParams({ date:histDate, from_hour:String(histFromHour), to_hour:String(histToHour), channel:String(histChannel), limit:'100' });
    const r = await fetch(`/api/dahua/history?${params}`, { headers:{ Authorization:`Bearer ${getToken()}` } });
    if (r.ok) { const d = await r.json(); setHistEvents(d.events||[]); }
    setHistLoading(false);
  }

  const noIntegrations = integrations.length === 0;

  // ── Render ───────────────────────────────────────────────────────────────────

  const TAB_STYLE = (active: boolean): React.CSSProperties => ({
    padding:'8px 18px', fontSize:13, fontWeight:600, borderRadius:6, border:'none',
    background: active ? '#3b82f6' : 'rgba(255,255,255,0.05)',
    color: active ? '#fff' : '#555555', cursor:'pointer', transition:'all 0.2s',
  });

  return (
    <Layout title="Smart Vision" subtitle="AI-powered video analytics — real-time detection, classification, and behavioural analysis">
      {editing && <CustomizerPanel widgets={VISION_WIDGETS} slots={slots} toggle={toggleVTab} moveUp={moveVUp} moveDown={moveVDown} reset={resetVLayout} onClose={() => setEditing(false)} />}
      <div style={{ padding:'24px 28px', maxWidth:1280, margin:'0 auto' }}>

        {/* Header actions */}
        <div style={{ display:'flex', justifyContent:'flex-end', alignItems:'center', marginBottom:20 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {pollingStatus==='polling' && <span style={{ fontSize:11, color:'#3b82f6' }}>● Polling…</span>}
            {pollingStatus==='error' && <span style={{ fontSize:11, color:'#ef4444' }}>● Error</span>}
            {lastPoll && <span style={{ fontSize:11, color:'#555555' }}>Updated {timeAgo(lastPoll.toISOString())}</span>}
            <div style={{ display:'flex', gap:4, alignItems:'center' }}>
              {orderedVWidgets.filter(w => isVTab(w.id)).map(w => {
                const t = w.id as 'live'|'history'|'analytics'|'map';
                const labels: Record<string,string> = { live:'🔴 Live', history:'🕐 History', analytics:'📊 Analytics', map:'🗺️ Density Map' };
                return <button key={t} onClick={() => setMainTab(t)} style={TAB_STYLE(mainTab===t)}>{labels[t]}</button>;
              })}
              <CustomizeButton editing={editing} setEditing={setEditing} />
            </div>
          </div>
        </div>

        {/* ── LIVE TAB ── */}
        {mainTab === 'live' && (
          <div>
            <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
              <EventTypeDropdown integrations={integrations} liveTypes={liveTypes} selected={selectedTypes} onChange={setSelectedTypes}/>
              {channels.length > 1 && (
                <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                  {channels.map(ch => (
                    <button key={ch} onClick={() => setSelectedChannels(prev => prev.includes(ch) ? prev.filter(x => x!==ch) : [...prev, ch])}
                      style={{ background: selectedChannels.includes(ch)?'rgba(59,130,246,0.3)':'rgba(255,255,255,0.05)', border:`1px solid ${selectedChannels.includes(ch)?'rgba(59,130,246,0.5)':'rgba(255,255,255,0.1)'}`, color: selectedChannels.includes(ch)?'#93c5fd':'#555555', padding:'4px 10px', borderRadius:4, fontSize:11, cursor:'pointer' }}>
                      CH{ch}
                    </button>
                  ))}
                </div>
              )}
              <span style={{ fontSize:12, color:'#555555', marginLeft:'auto' }}>{filtered.length} event{filtered.length!==1?'s':''}</span>
            </div>

            {filtered.length === 0 && (
              <div style={{ textAlign:'center', padding:'60px 20px', color:'#444444' }}>
                <div style={{ fontSize:40, marginBottom:12 }}>👁️</div>
                <div style={{ fontSize:16, fontWeight:600, marginBottom:4 }}>Waiting for events…</div>
                <div style={{ fontSize:13 }}>Events will appear here as they are detected by your cameras.</div>
              </div>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))', gap:12 }}>
              {filtered.map(evt => {
                const isExpanded = expandedId === evt.id;
                return (
                  <div key={evt.id} style={{ background:'#0d0d0d', border:`1px solid ${SEV_BG[evt.severity]}`, borderRadius:10, overflow:'hidden', transition:'box-shadow 0.2s' }}>
                    {evt.snapshot_url && (
                      <div style={{ height:120, overflow:'hidden', background:'#0d0d0d' }}>
                        <EventSnapshot url={evt.snapshot_url} alt={evt.camera_name}/>
                      </div>
                    )}
                    <div style={{ padding:'10px 12px' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                        <div>
                          <span style={{ fontSize:14 }}>{TYPE_ICON[evt.type]||'🔔'}</span>
                          <span style={{ fontSize:13, fontWeight:600, color:'#e5e5e5', marginLeft:6 }}>{evt.label}</span>
                        </div>
                        <span style={{ fontSize:10, fontWeight:700, letterSpacing:0.5, padding:'2px 7px', borderRadius:3, background:SEV_BG[evt.severity], color:SEV_COLOR[evt.severity], textTransform:'uppercase' }}>{evt.severity}</span>
                      </div>
                      <div style={{ fontSize:11, color:'#555555', marginTop:4 }}>{evt.camera_name} · {timeAgo(evt.timestamp)}</div>
                      <button onClick={() => setExpandedId(isExpanded ? null : evt.id)} style={{ background:'none', border:'none', color:'#3b82f6', fontSize:11, cursor:'pointer', padding:'4px 0', marginTop:2 }}>
                        {isExpanded ? 'Hide details ▲' : 'Show details ▼'}
                      </button>
                      {isExpanded && (
                        <div>
                          {Object.entries(evt.details).map(([k,v]) => (
                            <div key={k} style={{ fontSize:11, color:'#555555', marginTop:2 }}><span style={{ color:'#555555' }}>{k}:</span> {v}</div>
                          ))}
                          <EventClipPlayer event={evt}/>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── HISTORY TAB ── */}
        {mainTab === 'history' && (
          <div>
            <div style={{ display:'flex', gap:10, alignItems:'flex-end', marginBottom:16, flexWrap:'wrap' }}>
              <div>
                <div style={{ fontSize:11, color:'#555555', marginBottom:4 }}>Date</div>
                <input type="date" value={histDate} onChange={e => setHistDate(e.target.value)}
                  style={{ background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.12)', color:'#f1f5f9', borderRadius:6, padding:'6px 10px', fontSize:13 }}/>
              </div>
              <div>
                <div style={{ fontSize:11, color:'#555555', marginBottom:4 }}>From</div>
                <select value={histFromHour} onChange={e => setHistFromHour(Number(e.target.value))}
                  style={{ background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.12)', color:'#f1f5f9', borderRadius:6, padding:'6px 10px', fontSize:13 }}>
                  {Array.from({length:24},(_,h)=><option key={h} value={h}>{String(h).padStart(2,'0')}:00</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize:11, color:'#555555', marginBottom:4 }}>To</div>
                <select value={histToHour} onChange={e => setHistToHour(Number(e.target.value))}
                  style={{ background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.12)', color:'#f1f5f9', borderRadius:6, padding:'6px 10px', fontSize:13 }}>
                  {Array.from({length:24},(_,h)=><option key={h} value={h}>{String(h).padStart(2,'0')}:00</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize:11, color:'#555555', marginBottom:4 }}>Channel</div>
                <select value={histChannel} onChange={e => setHistChannel(Number(e.target.value))}
                  style={{ background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.12)', color:'#f1f5f9', borderRadius:6, padding:'6px 10px', fontSize:13 }}>
                  <option value={0}>All channels</option>
                  {Array.from({length:8},(_,i)=><option key={i+1} value={i+1}>Channel {i+1}</option>)}
                </select>
              </div>
              <button onClick={loadHistory} disabled={histLoading || noIntegrations}
                style={{ background:'#3b82f6', border:'none', color:'#fff', padding:'8px 18px', borderRadius:6, fontSize:13, fontWeight:600, cursor:'pointer', opacity: (histLoading||noIntegrations)?0.5:1 }}>
                {histLoading ? 'Loading…' : 'Search'}
              </button>
            </div>

            {histLoading && (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))', gap:12, marginBottom:12 }}>
                {Array.from({length: 6}).map((_, i) => (
                  <div key={i} style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:'12px 14px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                      <div className="skeleton" style={{ height:14, width:'60%' }} />
                      <div className="skeleton" style={{ height:14, width:'15%', borderRadius:3 }} />
                    </div>
                    <div className="skeleton" style={{ height:11, width:'40%' }} />
                  </div>
                ))}
              </div>
            )}
            {histEvents.length === 0 && !histLoading && (
              <div style={{ textAlign:'center', padding:'60px 20px', color:'#444444' }}>
                <div style={{ fontSize:32, marginBottom:8 }}>🕐</div>
                <div style={{ fontSize:15 }}>Select a date and time range, then click Search</div>
              </div>
            )}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))', gap:12 }}>
              {histEvents.map(evt => (
                <div key={evt.id} style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:'12px 14px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                    <span style={{ fontSize:13, fontWeight:600, color:'#e5e5e5' }}>{TYPE_ICON[evt.type]||'🔔'} {evt.label}</span>
                    <span style={{ fontSize:10, padding:'2px 6px', borderRadius:3, background:SEV_BG[evt.severity], color:SEV_COLOR[evt.severity] }}>{evt.severity}</span>
                  </div>
                  <div style={{ fontSize:11, color:'#555555' }}>{evt.camera_name} · {evt.timestamp}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── ANALYTICS TAB ── */}
        {mainTab === 'analytics' && (
          <div>
            {/* Sample data notice */}
            <div style={{ backgroundColor:'#1a1a1a', border:'1px solid #333', padding:'6px 12px', fontSize:'9px', color:'#555', marginBottom:'14px', display:'flex', alignItems:'center', gap:'8px' }}>
              <span style={{ color:'#444', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px' }}>ANALYTICS</span>
              <span>— Sample data shown. Connect a compatible AI camera or VMS to enable live analytics.</span>
            </div>
            {/* Sub-tabs */}
            <div style={{ display:'flex', gap:6, marginBottom:20, flexWrap:'wrap', alignItems:'center' }}>
              {ANALYTICS_TABS.map(t => (
                <button key={t} onClick={() => setAnalyticsTab(t)} style={{ ...TAB_STYLE(analyticsTab===t), fontSize:12, padding:'6px 14px' }}>{t}</button>
              ))}
              <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center' }}>
                <input type="date" value={analyticsDate} onChange={e => setAnalyticsDate(e.target.value)}
                  style={{ background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.12)', color:'#f1f5f9', borderRadius:6, padding:'5px 10px', fontSize:12 }}/>
                <button
                  onClick={() => { const btn = document.activeElement as HTMLButtonElement; btn.textContent='✓ Exported'; setTimeout(() => { btn.textContent='↓ Export CSV'; }, 2000); }}
                  style={{ padding:'5px 14px', background:'rgba(34,197,94,0.12)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:6, color:'#22c55e', fontSize:11, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' as const }}>
                  ↓ Export CSV
                </button>
              </div>
            </div>

            {/* Overview */}
            {analyticsTab === 'Overview' && (
              <div>
                <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
                  <StatCard label="Total People Today" value={totalPeople} sub="All cameras combined"/>
                  <StatCard label="Entries" value={totalIn} sub="Through all access points" color="#22c55e"/>
                  <StatCard label="Exits" value={totalOut} sub="Through all access points" color="#ef4444"/>
                  <StatCard label="Loitering Alerts" value={loiteringAlerts.length} sub="Today" color="#f59e0b"/>
                  <StatCard label="Active Cameras" value={integrations.length > 0 ? '8' : '0'} sub={integrations.length > 0 ? 'Live feeds' : 'No integration'}/>
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                  <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:16 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:'#555555', letterSpacing:1, textTransform:'uppercase', marginBottom:12 }}>People Flow — Hourly</div>
                    <PeopleFlowChart date={analyticsDate}/>
                  </div>
                  <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:16 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:'#555555', letterSpacing:1, textTransform:'uppercase', marginBottom:12 }}>Gender Breakdown</div>
                    {(['male','female','unknown'] as const).map(g => (
                      <MiniBar key={g} label={g.charAt(0).toUpperCase()+g.slice(1)} value={genderBreakdown[g]}
                        max={Math.max(genderBreakdown.male, genderBreakdown.female, genderBreakdown.unknown)}
                        color={g==='male'?'#3b82f6':g==='female'?'#ec4899':'#555555'}/>
                    ))}
                    <div style={{ fontSize:12, fontWeight:700, color:'#555555', letterSpacing:1, textTransform:'uppercase', margin:'16px 0 10px' }}>Age Groups</div>
                    {(Object.entries(AGE_GROUP_LABELS) as [string,string][]).map(([key, label]) => (
                      <MiniBar key={key} label={label} value={(ageBreakdown as any)[key]}
                        max={Math.max(...Object.values(ageBreakdown))} color="#8b5cf6"/>
                    ))}
                  </div>
                </div>

                <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:16, marginTop:16 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'#555555', letterSpacing:1, textTransform:'uppercase', marginBottom:12 }}>Top Clothing Colors</div>
                  <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                    {clothingColors.map(c => (
                      <div key={c.color} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                        <div style={{ width:36, height:36, borderRadius:6, background:c.hex, border:'2px solid rgba(255,255,255,0.1)' }}/>
                        <div style={{ fontSize:10, color:'#a0a0a0' }}>{c.color}</div>
                        <div style={{ fontSize:11, fontWeight:700, color:'#f1f5f9' }}>{c.pct}%</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* People */}
            {analyticsTab === 'People' && (
              <div>
                <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap' }}>
                  <StatCard label="Detections" value={personDetections.length} sub="Today"/>
                  <StatCard label="Loitering" value={personDetections.filter(d=>d.isLoitering).length} sub="Dwell >3 min" color="#f59e0b"/>
                  <StatCard label="With Bags" value={personDetections.filter(d=>d.hasBag||d.hasBackpack).length} sub="Carrying items"/>
                  <StatCard label="Avg. Dwell" value={`${Math.round(personDetections.reduce((s,d)=>s+d.dwellSeconds,0)/personDetections.length)}s`} sub="All detections"/>
                </div>
                <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:16 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'#555555', letterSpacing:1, textTransform:'uppercase', marginBottom:12 }}>Detection Log</div>
                  {personDetections.map(d => <PersonAttributeRow key={d.id} d={d}/>)}
                </div>
              </div>
            )}

            {/* Heatmap */}
            {analyticsTab === 'Heatmap' && (
              <div>
                <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:16, marginBottom:16 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'#555555', letterSpacing:1, textTransform:'uppercase', marginBottom:12 }}>Crowd Density Heatmap — All Zones</div>
                  <HeatmapGrid seed={analyticsDate.split('').reduce((a,c)=>a+c.charCodeAt(0),0)}/>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                  {ZONES.slice(0,4).map((z,i) => (
                    <div key={z} style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:16 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:'#555555', letterSpacing:1, textTransform:'uppercase', marginBottom:10 }}>{z}</div>
                      <HeatmapGrid seed={i * 17 + analyticsDate.charCodeAt(0)}/>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Flow */}
            {analyticsTab === 'Flow' && (
              <div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                  <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:16 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:'#555555', letterSpacing:1, textTransform:'uppercase', marginBottom:14 }}>Directional Flow</div>
                    <DirectionalFlowPanel/>
                  </div>
                  <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:16 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:'#555555', letterSpacing:1, textTransform:'uppercase', marginBottom:14 }}>Hourly Entry/Exit</div>
                    <PeopleFlowChart date={analyticsDate}/>
                  </div>
                </div>
                <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:16, marginTop:16 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'#555555', letterSpacing:1, textTransform:'uppercase', marginBottom:12 }}>Zone Breakdown</div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:12 }}>
                    {ZONES.map((z, i) => {
                      const cnt = generateHourlyPeopleCounts(analyticsDate, z).reduce((s,r)=>s+r.count,0);
                      return (
                        <div key={z} style={{ background:'#111111', borderRadius:8, padding:12, textAlign:'center' }}>
                          <div style={{ fontSize:11, color:'#555555', marginBottom:4 }}>{z}</div>
                          <div style={{ fontSize:22, fontWeight:700, color:'#3b82f6' }}>{cnt}</div>
                          <div style={{ fontSize:10, color:'#555555' }}>people</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Loitering */}
            {analyticsTab === 'Loitering' && (
              <div>
                <div style={{ display:'flex', gap:12, marginBottom:16 }}>
                  <StatCard label="Loitering Alerts" value={loiteringAlerts.length} sub="Today" color="#f59e0b"/>
                  <StatCard label="High Severity" value={loiteringAlerts.filter(a=>a.severity==='high').length} color="#ef4444" sub="Immediate review required"/>
                  <StatCard label="Avg. Dwell" value={`${Math.round(loiteringAlerts.reduce((s,a)=>s+a.dwellSeconds,0)/(loiteringAlerts.length||1)/60)}m`} sub="Per loitering event"/>
                </div>
                <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:16 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'#555555', letterSpacing:1, textTransform:'uppercase', marginBottom:12 }}>Loitering Detections</div>
                  <LoiteringPanel alerts={loiteringAlerts}/>
                </div>
                <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:16, marginTop:16 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'#555555', letterSpacing:1, textTransform:'uppercase', marginBottom:12 }}>Persons with Extended Dwell</div>
                  {personDetections.filter(d=>d.isLoitering).map(d => <PersonAttributeRow key={d.id} d={d}/>)}
                </div>
              </div>
            )}

            {/* Timeline */}
            {analyticsTab === 'Timeline' && (
              <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:16 }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#555555', letterSpacing:1, textTransform:'uppercase', marginBottom:12 }}>Event Timeline — {analyticsDate}</div>
                <TimelinePanel events={timelineEvents}/>
              </div>
            )}
          </div>
        )}

        {/* ── DENSITY MAP TAB ── */}
        {mainTab === 'map' && (
          <div>
            <div style={{ fontSize:11, color:'#a0a0a0', marginBottom:12, background:'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.15)', borderRadius:8, padding:'8px 12px' }}>
              <span style={{ color:'#3b82f6', fontWeight:700 }}>Live Density Map</span>
              {' '}— Real-time people counting overlaid on building floor plan. Zone colours indicate current occupancy density. Data sourced from Smart Vision AI cameras.
            </div>
            <SiteMap mode="vision" height={470} />
            <div style={{ marginTop:14, display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10 }}>
              {[
                { zone:'Office Zone A', count:72, cap:100, trend:'+3' },
                { zone:'Office Zone B', count:58, cap:80,  trend:'+1' },
                { zone:'Lobby',         count:55, cap:80,  trend:'-2' },
                { zone:'Car Park',      count:42, cap:100, trend:'+5' },
              ].map(z => (
                <div key={z.zone} style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:12 }}>
                  <div style={{ fontSize:11, color:'#555555', marginBottom:6 }}>{z.zone}</div>
                  <div style={{ fontSize:22, fontWeight:700, color: z.count/z.cap>0.8?'#ef4444':z.count/z.cap>0.6?'#f59e0b':'#22c55e', marginBottom:4 }}>{z.count}</div>
                  <div style={{ fontSize:10, color:'#555555' }}>/ {z.cap} capacity</div>
                  <div style={{ marginTop:6, background:'rgba(255,255,255,0.06)', borderRadius:3, height:4 }}>
                    <div style={{ width:`${(z.count/z.cap)*100}%`, height:'100%', borderRadius:3, background: z.count/z.cap>0.8?'#ef4444':z.count/z.cap>0.6?'#f59e0b':'#22c55e' }}/>
                  </div>
                  <div style={{ fontSize:10, color:'#3b82f6', marginTop:4 }}>Trend: {z.trend} last hour</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
